import { Stack } from "@mui/material";
import { useAtom, useAtomValue } from "jotai";
import { useMemo } from "react";
import useUpdateReleaseTrack from "../../../../api/releases/hooks/useUpdateReleaseTrack";
import {
  SupplyChainLanguage,
  SupplyChainParentalAdvisory,
  SupplyChainPrimaryGenre,
} from "../../../../api/supplyChain/supplyChainModels";
import {
  editReleaseTrackFormAtom,
  isEditReleaseTrackFormValid,
} from "../../../../atoms/forms/editReleaseTrackFormAtom";
import { useGetFailedTrackFileStatus } from "../../../../atoms/supplychain/supplyChainFailedUploadsAtom";
import { Button } from "../../../core-ui/components/Button/Button";
import { ButtonVariant } from "../../../core-ui/components/Button/utils";
import { Text } from "../../../core-ui/components/Text/Text";
import { TextStyleVariant } from "../../../core-ui/components/Text/TextUtils";
import Form from "../../../elements/Form/Form";
import FormOutlinedTextField from "../../../elements/Form/FormOutlinedTextField";
import FormSelect from "../../../elements/Form/FormSelect";
import {
  convertSupplyChainOptions,
  convertSupplyChainParentalAdvisoryOptions,
} from "../../../forms/NewReleaseForm/utils";
import { AlertCard } from "../../../screens/Error/components/AlertCard";
import { handleUpdateReleaseTrackForm } from "./utils";

interface EditReleaseTrackFormProps {
  handleCancel: () => void;
  handleSave: () => void;
  releaseTrackId: number;
  genres: SupplyChainPrimaryGenre[];
  languages: SupplyChainLanguage[];
  parentalAdvisories: SupplyChainParentalAdvisory[];
}

export const EditReleaseTrackForm = ({
  handleCancel,
  handleSave,
  releaseTrackId,
  genres,
  languages,
  parentalAdvisories,
}: EditReleaseTrackFormProps) => {
  const [formState, setFormState] = useAtom(editReleaseTrackFormAtom);
  const isFormValid = useAtomValue(isEditReleaseTrackFormValid);
  const { mutateAsync: updateReleaseTrack, isPending: isSaving } =
    useUpdateReleaseTrack(releaseTrackId);
  const failedUpload = useGetFailedTrackFileStatus(releaseTrackId || -1);

  const filteredLanguages = useMemo(
    () => convertSupplyChainOptions(languages),
    [languages],
  );

  const filteredGenres = useMemo(
    () => convertSupplyChainOptions(genres),
    [genres],
  );

  const filteredSecondaryGenres = useMemo(() => {
    if (!formState.genre) return null;
    const selectedGenre = genres.find(
      (genre) => genre.id === parseInt(formState.genre),
    );
    if (!selectedGenre || !selectedGenre.children.length) return null;
    return convertSupplyChainOptions(selectedGenre.children);
  }, [formState.genre, genres]);

  const filteredParentalAdvisories = useMemo(
    () => convertSupplyChainParentalAdvisoryOptions(parentalAdvisories),
    [parentalAdvisories],
  );
  return (
    <Stack gap={3}>
      <Text component="h1" variant={TextStyleVariant.H4}>
        Track Details
      </Text>
      {failedUpload && (
        <AlertCard
          message={`Error: ${failedUpload.errorMessage}`}
          fullWidth
          margin={0}
        />
      )}
      <Form
        onSubmit={async (e) => {
          e.preventDefault();
          if (isFormValid && !isSaving) {
            await handleUpdateReleaseTrackForm(
              formState,
              updateReleaseTrack,
            ).then(() => handleSave());
          }
        }}
      >
        <Stack gap={3}>
          <FormOutlinedTextField
            id="release-track-song-title"
            label="Song Title"
            name="songTitle"
            fullWidth
            required
            value={formState.songTitle}
            onChange={(e) => setFormState({ songTitle: e.target.value })}
          />
          <FormOutlinedTextField
            id="release-track-artist-name"
            label="Artist Name"
            name="artistName"
            fullWidth
            required
            disabled // TODO: Remove when artist name is added to ReleaseTrack model
            value={formState.artistName}
            onChange={(e) => setFormState({ artistName: e.target.value })}
          />
          <FormOutlinedTextField
            id="release-track-featured-artists"
            label="Featured Artists (add a comma between each artist's name)"
            name="featuredArtists"
            fullWidth
            value={formState.featuredArtists}
            onChange={(e) => setFormState({ featuredArtists: e.target.value })}
          />
          <FormSelect
            label="Primary Genre"
            id="create-release-genre-input"
            name={"genre"}
            fullWidth
            required
            value={formState.genre}
            onChange={(e) =>
              setFormState({ genre: e.target.value, secondaryGenre: "" })
            }
            items={filteredGenres}
            slotProps={{
              select: {
                renderValue: (selected) =>
                  filteredGenres[parseInt(selected as string) ?? 1],
              },
            }}
          />
          <FormSelect
            label="Secondary Genre"
            id="create-release-secondary-genre-input"
            name="secondaryGenre"
            fullWidth
            required
            value={formState.secondaryGenre}
            onChange={(e) => setFormState({ secondaryGenre: e.target.value })}
            items={filteredSecondaryGenres || []}
            slotProps={{
              select: {
                renderValue: (selected) =>
                  filteredSecondaryGenres?.[parseInt(selected as string)],
              },
            }}
            disabled={!filteredSecondaryGenres}
          />
          <FormSelect
            label="Language"
            id="create-release-language-input"
            name={"language"}
            fullWidth
            required
            value={formState.language}
            onChange={(e) => setFormState({ language: e.target.value })}
            items={filteredLanguages}
            slotProps={{
              select: {
                renderValue: (selected) =>
                  filteredLanguages[parseInt(selected as string) ?? 1],
              },
            }}
          />
          <FormSelect
            label="Parental Advisory"
            id="create-release-parental-advisory-input"
            name={"parentalAdvisory"}
            fullWidth
            required
            value={formState.parentalAdvisory}
            onChange={(e) => setFormState({ parentalAdvisory: e.target.value })}
            items={filteredParentalAdvisories}
            slotProps={{
              select: {
                renderValue: (selected) =>
                  filteredParentalAdvisories[parseInt(selected as string) ?? 1],
              },
            }}
          />
          <Stack direction="row" justifyContent="center" gap={2}>
            <Button
              fullWidth
              variant={ButtonVariant.BLACK_OUTLINED_BUTTON_BLACK_HOVER}
              onClick={() => handleCancel()}
            >
              Cancel
            </Button>
            <Button
              fullWidth
              disabled={!isFormValid || isSaving}
              loading={isSaving}
              type="submit"
            >
              Save Song
            </Button>
          </Stack>
        </Stack>
      </Form>
    </Stack>
  );
};
